import {
  pollUpdatePhoneStatus,
  updatePhone,
  type UpdatePhoneInput,
  type UpdateProfileOutput,
  UpdateProfileStatus,
  useCustomerDetails,
} from '@egw/api'
import { DEFAULT_COUNTRY_CODE } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { zodResolver } from '@hookform/resolvers/zod'
import { type Dispatch, type FC, type SetStateAction, useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { z, type ZodType } from 'zod'
import {
  UpdatePhoneField,
  type UpdatePhoneForm,
  type UpdatePhoneStep,
  UpdatePhoneStepEnum,
} from '../../types/phone/phone-types'
import { removeCountryCodeFromPhoneNumber } from '../../utils/update-phone/update-phone-utils'
import { ChangePhoneStep } from './change-phone-step'
import { ConfirmPhoneStep } from './confirm-phone-step'
import { ErrorStep } from './error-step'
import { VerifyPhoneStep } from './verify-phone-step'

const log = new Logger('Update Phone')

export const UpdatePhone: FC<{
  closeModal: () => void
  setModalTitle: Dispatch<SetStateAction<string>>
}> = ({ setModalTitle, closeModal }) => {
  const { t } = useTranslation('features-settings')

  const [step, setStep] = useState<UpdatePhoneStepEnum>(UpdatePhoneStepEnum.CHANGE)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { refetch: refetchCustomerDetails } = useCustomerDetails()

  const onErrorCb = useCallback((e: unknown) => {
    log.error('On Error CB invoked ', e)
    // change to error modal step or launch toast here
    setStep(UpdatePhoneStepEnum.ERROR)
  }, [])

  const onSuccessCb = async (data: UpdateProfileOutput): Promise<void> => {
    if (data.__typename === 'UpdateProfileSuccess') {
      // eslint-disable-next-line testing-library/no-debugging-utils
      log.debug('On Success CB', data)
      if (data.status === UpdateProfileStatus.VERIFICATION_SENT) {
        setStep(UpdatePhoneStepEnum.VERIFY)
        setModalTitle(t(`update_phone.${UpdatePhoneStepEnum.VERIFY}.title`))
      }
      if (data.status === UpdateProfileStatus.PENDING_VERIFICATION) {
        // Will start polling API
        // eslint-disable-next-line testing-library/no-debugging-utils
        log.debug('Pending Verification')

        await pollUpdatePhoneStatus().then(onSuccessCb).catch(onErrorCb)
      }
      if (data.status === UpdateProfileStatus.COMPLETE) {
        if (step !== UpdatePhoneStepEnum.CONFIRM) {
          void refetchCustomerDetails()
          setStep(UpdatePhoneStepEnum.CONFIRM)
          setModalTitle(t(`update_phone.${UpdatePhoneStepEnum.CONFIRM}.title`))
        }
      }
      if (data.status === UpdateProfileStatus.FAILED) onErrorCb(data)
    } else {
      onErrorCb(data)
    }
  }

  const updatePhoneValidationSchema: ZodType<UpdatePhoneForm> = z.object({
    [UpdatePhoneField.PHONE]: z.string({
      required_error: t(`udpate_phone.${step}.required`),
    }),
    [UpdatePhoneField.OTP_CODE]: z
      .string({
        required_error: t(`update_phone.${step}.otp_required`),
      })
      .length(6, t(`update_phone.${step}.otp_required`)),
  })

  const formMethods = useForm<UpdatePhoneForm>({
    defaultValues: {
      [UpdatePhoneField.PHONE]: '',
      [UpdatePhoneField.OTP_CODE]: '',
    },
    resolver: zodResolver(updatePhoneValidationSchema),
  })

  const onSubmit = async (): Promise<void> => {
    // Build Mutation Arguments Per Step
    const updatePhoneInput: UpdatePhoneInput = {
      code: step === UpdatePhoneStepEnum.VERIFY ? formMethods.getValues(UpdatePhoneField.OTP_CODE) : undefined,
      phone:
        step === UpdatePhoneStepEnum.CHANGE
          ? {
              phoneNumber: removeCountryCodeFromPhoneNumber(formMethods.getValues(UpdatePhoneField.PHONE)),
              countryPhoneCode: DEFAULT_COUNTRY_CODE,
            }
          : undefined,
    }

    setIsLoading(true)

    try {
      await updatePhone(updatePhoneInput).then(onSuccessCb).catch(onErrorCb)
    } catch (error) {
      onErrorCb(error)
    } finally {
      setIsLoading(false)
    }
  }

  const resendCode = (): void => {
    log.info('Resend Code invoked')
    setIsLoading(true)
    try {
      updatePhone({
        resendVerification: true,
        phone: {
          phoneNumber: removeCountryCodeFromPhoneNumber(formMethods.getValues(UpdatePhoneField.PHONE)),
          countryPhoneCode: DEFAULT_COUNTRY_CODE,
        },
      }).catch(onErrorCb)
    } catch (error) {
      onErrorCb(error)
    } finally {
      setIsLoading(false)
    }
  }

  const props: UpdatePhoneStep = {
    isLoading,
    ctaOnClick: onSubmit,
    resendCode,
    closeModal,
  }

  return (
    <FormProvider {...formMethods}>
      <form className="w-full" id="updatePhone">
        {/* {getContent()} */}
        {step === UpdatePhoneStepEnum.CHANGE && <ChangePhoneStep {...props} />}
        {step === UpdatePhoneStepEnum.VERIFY && <VerifyPhoneStep {...props} />}
        {step === UpdatePhoneStepEnum.CONFIRM && <ConfirmPhoneStep {...props} />}
        {step === UpdatePhoneStepEnum.ERROR && <ErrorStep />}
      </form>
    </FormProvider>
  )
}
